import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { getTokenFromFirebase } from "./Services/firebase";
import { setChatServicesData } from "./Store/Slices/ChatServicesSlice";
import {
  clearDeviceToken,
  firebaseSelector,
  setDeviceToken,
  setNotificationStatus,
} from "./Store/Slices/FirebaseSlice";
import { jwtToken as jwtTokenAction } from "./Store/Thunks/ChatSocketThunk";
import { addUserNotificationToken } from "./Store/Thunks/FirebaseThunk";
import { getCookie } from "./Utils/cookiesHelper";
import {
  initializeWebSocketConnection,
} from "./Store/Slices/AuthSlice";
import { getUserChatData, getUserInfo } from "./Store/Thunks/AuthThunk";
import StompChatService from "./Services/StompChatService";
import { enableSdkBridge, subscribeSdkActions, unsubscribeSdkActions } from "./Services/SdkBridge";
import Utils, {
  getDataHandling,
  getOriginUrlAndOrgId,
  handleChatImages,
  postMessage,
} from "./Utils/utils";

import SideServicesApp from "./Components/SideServicesApp/SideServicesApp";
import AppBase from "./Components/AppBase/AppBase";
import AppContainer from "./Components/AppContainer/AppContainer";
import ServicesApp from "./Components/ServicesApp/ServicesApp";
import ErrorBoundary from "./Components/ErrorBoundary/ErrorBoundary";
import AppRouter from "./Pages/routes";
import { ThemeProvider } from "./Context/ThemeContext";
import { getResponsiveOpenWidth, getResponsiveOpenHeight } from "./config/widgetConfig";

import "./App.scss";

export default function App() {
  // Memoize URL params to prevent unnecessary re-renders
  const {
    orgId,
    originUrl,
    preview: chatPreview,
    dashboardToken,
  } = useMemo(() => getOriginUrlAndOrgId(), []);

  const dispatch = useDispatch();
  const { deviceToken, notificationStatus } = useSelector(firebaseSelector);
  const { isLoggedIn, token, user, userChatData } = useSelector(
    (state) => state.auth
  );
  const {
    open,
    org,
    chatSettingsData,
    shopHas360,
    shops,
    isLoading,
    preview,
  } = useSelector((state) => state.chatServicesData);

  // Get chat socket state for smart launcher mode detection
  const { messages = [], agentsOnline = [] } = useSelector((state) => state.chatSocket || {});

  // Chat
  // Step 1: get jwt token
  useEffect(() => {
    const hasJwtToken = getCookie("jwtToken");
    if (token && !hasJwtToken) {
      dispatch(jwtTokenAction(token));
    }
  }, [token, dispatch]);

  // Step 2: Fetch fresh user info on login (ensures profile image is up-to-date across devices)
  useEffect(() => {
    if (isLoggedIn && token) {
      dispatch(getUserInfo());
    }
  }, [isLoggedIn, token, dispatch]);

  // Step 3: get user chat data after login
  useEffect(() => {
    if (isLoggedIn && token && user?.id && !userChatData) {
      dispatch(getUserChatData({ userId: user.id, token }));
    }
  }, [isLoggedIn, token, user?.id, userChatData, dispatch]);

  // Step 4: Initialize WebSocket connection if token exists
  useEffect(() => {
    if (isLoggedIn && token) {
      dispatch(initializeWebSocketConnection());
    }
  }, [isLoggedIn, token, dispatch]);

  // Auto-connect WebSocket when token exists but user is not yet logged in (persisted session)
  useEffect(() => {
    const existingToken = getCookie("jwtToken");
    if (existingToken && !isLoggedIn) {
      StompChatService.connect(
        existingToken,
        null,
        null, // Message handlers registered via StompChatService.onMessage()
        null
      );
    }
  }, []);

  const setFirebaseToken = (value) => {
    dispatch(setDeviceToken(value));
  };

  useEffect(() => {
    if (deviceToken && token) {
      dispatch(addUserNotificationToken(deviceToken));
    }
  }, [deviceToken, token, isLoggedIn, dispatch]);

  useEffect(() => {
    if (notificationStatus === "granted") {
      getTokenFromFirebase(setFirebaseToken);
    }
    if (notificationStatus === "denied") {
      toast.error(
        "Notification is off, please allow notifications to be able to get all features"
      );
      dispatch(clearDeviceToken());
    }
  }, [notificationStatus, dispatch]);

  const fetchData = async () => {
    try {
      let { orgData, chatSettingsData, shopsData, shopHas360 } =
        await getDataHandling({
          orgId,
          preview: chatPreview,
          dashboardToken,
        });

      chatSettingsData = handleChatImages({
        chatSettingsData,
        orgData,
      });

      Utils.setWidgetColors(chatSettingsData?.color);
      document.body.setAttribute("data-theme", chatSettingsData?.mode);

      dispatch(
        setChatServicesData({
          org: orgData,
          shops: shopsData,
          shopHas360,
          isLoading: false,
          appUrl: originUrl,
          chatSettingsData,
          preview: chatPreview,
        })
      );
      onAppInit();
      postMessage({ type: "org", payload: { org: orgData } });
      Utils.set360Cookies();
    } catch (err) {
      postMessage({
        type: "invalidOrg",
        payload: { org: err?.response?.data },
      });
      dispatch(setChatServicesData({ isLoading: false }));
    }
  };

  // Fetch data once on mount (guard against missing orgId from direct URL access)
  useEffect(() => {
    if (orgId) fetchData();
  }, [orgId]);

  // Listen for postMessage from parent window (dashboard preview)
  useEffect(() => {
    const messageHandler = (event) => {
      // Validate origin — only accept messages from the embedding parent
      if (originUrl && originUrl !== "*") {
        try {
          if (event.origin !== new URL(originUrl).origin) return;
        } catch (e) {
          return;
        }
      }

      // Parse message - handle both string and object formats
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return; // Not JSON, skip
        }
      }

      // Skip non-object messages
      if (!data || typeof data !== 'object') return;

      if (data.type === "notification-permission-response") {
        dispatch(setNotificationStatus(data.permission));
      }

      // Handle settings update from dashboard (published)
      if (data.type === "settings-updated" || data.type === "settingsUpdated") {
        fetchData();
      }

      // Handle live preview updates (unpublished changes)
      if (data.type === "preview-settings-update") {
        if (data.payload) {
          const payloadCopy = JSON.parse(JSON.stringify(data.payload));
          const newSettings = handleChatImages({
            chatSettingsData: payloadCopy,
            orgData: org,
          });
          Utils.setWidgetColors(newSettings?.color);
          document.body.setAttribute("data-theme", newSettings?.mode);
          dispatch(setChatServicesData({ chatSettingsData: { ...newSettings } }));
        }
      }
    };

    window.addEventListener("message", messageHandler);
    return () => window.removeEventListener("message", messageHandler);
  }, [dispatch, org]);

  // Initialize WebSocket connection for settings updates (works for all users, logged in or not)
  // ONLY for live widgets, NOT for preview iframes in dashboard
  useEffect(() => {
    if (!orgId || chatPreview) return; // Skip WebSocket for preview mode

    const wsStatus = StompChatService.getConnectionStatus();

    // If not connected, connect for settings subscription (no auth token needed for public topics)
    if (!wsStatus.connected) {
      StompChatService.connect(null, null, null, null);
    }
  }, [orgId, chatPreview]);

  // Subscribe to real-time settings updates via WebSocket
  // ONLY for live widgets, NOT for preview iframes in dashboard
  useEffect(() => {
    if (!orgId || chatPreview) return; // Skip WebSocket for preview mode

    // Handler for settings update notifications
    const handleSettingsUpdate = (payload) => {
      if (payload.action === "published") {
        // Re-fetch settings when admin publishes new ones
        fetchData();
        // Notify parent window about settings update
        postMessage({ type: "settingsUpdated", payload });
      }
    };

    // Subscribe to settings updates — StompChatService handles pending
    // callbacks internally if WebSocket isn't connected yet
    const subscribeTimeout = setTimeout(() => {
      StompChatService.subscribeSettings(orgId, handleSettingsUpdate);
      subscribeSdkActions(orgId);
    }, 500);

    return () => {
      clearTimeout(subscribeTimeout);
      // Cleanup subscriptions on unmount
      StompChatService.unsubscribeSettings();
      unsubscribeSdkActions();
    };
  }, [orgId, chatPreview]);

  // Handle window resize messages from parent
  useEffect(() => {
    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, []);

  useEffect(() => {
    if (org?.id && preview && chatPreview) {
      dispatch(setChatServicesData({ open }));
    }

    if (open === true && window.screen.width < 900) {
      const message = { type: "setParentStyle" };
      postMessage(message);
    }

    if (!open && window.screen.width < 900) {
      const message = { type: "resetParentStyle" };
      postMessage(message);
    }
  }, [org, open]);

  // handle floating widget styles
  useEffect(() => {
    if (!open && chatSettingsData.widgetFloating) {
      const message = { type: "floatingWidgetStyle" };
      postMessage(message);
    }

    if (!open && !chatSettingsData.widgetFloating) {
      const message = { type: "resetFloatingWidgetStyle" };
      postMessage(message);
    }
  }, [org, chatSettingsData, open]);

  // to open on the first time on preview
  useEffect(() => {
    if (org?.id && preview && chatPreview) {
      dispatch(setChatServicesData({ open: true }));
    }
  }, [org]);

  const onAppInit = () => {
    const message = { type: "init", payload: {} };
    postMessage(message);
  };

  const receiveMessage = (event) => {
    const { originUrl } = getOriginUrlAndOrgId();

    // Compare origin only (not full URL) to handle SDK messages from parent page
    let expectedOrigin = originUrl;
    if (originUrl && originUrl !== "*") {
      try { expectedOrigin = new URL(originUrl).origin; } catch (e) {}
    }
    if (event.origin === expectedOrigin || expectedOrigin === "*") {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === "windowSize") {
          const { width } = data.payload;
          dispatch(setChatServicesData({ width }));
        }
        if (data.type === "sdk-init") {
          enableSdkBridge();
          if (orgId) subscribeSdkActions(orgId);
        }
        if (data.type === "sdk-open") {
          dispatch(setChatServicesData({ open: true }));
        }
        if (data.type === "sdk-close") {
          dispatch(setChatServicesData({ open: false }));
        }
        if (data.type === "sdk-page-context") {
          dispatch(setChatServicesData({ pageContext: data.payload }));
        }
        if (data.type === "sdk-send-message") {
          dispatch(setChatServicesData({ open: true, pendingMessage: data.payload?.content }));
        }
      } catch (e) {
        // Non-JSON message, ignore
      }
    }
  };

  const toggleServiceModal = () => {
    if (open) {
      const message = {
        type: "dimensions",
        payload: { width: "350px", height: "160px" },
      };
      postMessage(message);
    } else {
      const message = {
        type: "dimensions",
        payload: { width: getResponsiveOpenWidth(), height: getResponsiveOpenHeight() },
      };
      postMessage(message);
    }
    dispatch(setChatServicesData({ open: !open }));
  };

  // Determine smart launcher mode based on conversation and agents
  const launcherMode = useMemo(() => {
    const recentResponses = messages
      .filter(m => m.senderType === 'AGENT' || m.senderType === 'agent' || m.senderType === 'BOT' || m.senderType === 'bot')
      .slice(-10);

    const uniqueAgents = new Set(
      recentResponses
        .filter(m => (m.senderType === 'AGENT' || m.senderType === 'agent') && m.sender?.id)
        .map(m => m.sender.id)
    );

    const hasRecentHumanResponse = recentResponses.some(m =>
      m.senderType === 'AGENT' || m.senderType === 'agent'
    );

    if (hasRecentHumanResponse) {
      if (uniqueAgents.size > 1) return 'team';
      return 'human';
    }

    if (agentsOnline && agentsOnline.length > 1) return 'team';
    if (agentsOnline && agentsOnline.length === 1) return 'human';

    return 'ai';
  }, [messages, agentsOnline]);

  const app = (
    <>
      {open && (
        <AppContainer>
          {isLoading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: "12px", fontFamily: "Inter, Manrope, sans-serif" }}>
              <div className="app-loading-spinner" />
              <p style={{ fontSize: "13px", color: "#888" }}>Loading...</p>
            </div>
          ) : !org?.id && !chatPreview ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: "8px", padding: "24px", textAlign: "center", fontFamily: "Inter, Manrope, sans-serif" }}>
              <p style={{ fontSize: "14px", color: "#666" }}>Unable to load chat widget.</p>
              <p style={{ fontSize: "12px", color: "#999" }}>Please check the configuration and try again.</p>
            </div>
          ) : (
            <ErrorBoundary>
              <AppRouter
                org={org}
                chatSettings={chatSettingsData}
                shopHas360={shopHas360}
                shops={shops?.content || []}
                preview={chatPreview}
              />
            </ErrorBoundary>
          )}
        </AppContainer>
      )}
    </>
  );

  return (
    <ThemeProvider>
      <AppBase app={app} open={open} chatSettings={chatSettingsData}>
        {!open && (
          <>
            {chatSettingsData?.widgetFloating ? (
              <SideServicesApp
                onClick={toggleServiceModal}
                isLoading={isLoading}
              />
            ) : (
              <ServicesApp
                open={open}
                title={chatSettingsData?.title}
                imageUrl={chatSettingsData?.images?.logo_url}
                onClick={toggleServiceModal}
                isLoading={isLoading}
                mode={launcherMode}
              />
            )}
          </>
        )}
      </AppBase>
    </ThemeProvider>
  );
}
